<template>
  <div
    :class="{
      'device-teaser': true,
      'device-teaser--not-reachable': !device.isReachable,
    }"
  >
    <div class="device-teaser__left">
      <img :src="imageUrl" class="device-teaser__img" />
    </div>
    <div class="device-teaser__center">
      <h2 class="device-teaser__title">
        {{ device.name }}
      </h2>
      <div v-if="device.description" class="device-teaser__description">
        {{ device.description }}
      </div>
    </div>
    <div class="device-teaser__right" @click.capture.stop="">
      <div
        v-if="device.isReachable === 'unknown'"
        class="device-teaser__spinner"
      >
        <Spinner />
      </div>
      <div
        v-else-if="
          showClockWhenTimerIsActive &&
          $store.getters.deviceTimerIsActive(device.id)
        "
        class="device-teaser__timer-icon"
        :title="str('devices.timerActiveCantSwitch')"
      >
        <Clock />
      </div>
      <JsToggle
        v-else-if="device.isReachable === true"
        :color="primaryColor"
        :sync="true"
        :value="device.isOn"
        @change="$store.dispatch('toggleDeviceOn', device)"
        :height="30"
        :disabled="
          $store.getters.formState('deviceOn_' + device.id) === 'submitting'
        "
        :title="
          this.device.isOn
            ? this.str('devices.turnOff')
            : this.str('devices.turnOn')
        "
      />
    </div>
    <div
      v-if="device.isReachable === true"
      :class="{
        'device-teaser__current-temperature': true,
        'device-teaser__current-temperature--right': consumption === '',
      }"
      :title="
        str('devices.currentTemperature', {
          '@value': temperature,
        })
      "
    >
      {{ temperature }}
    </div>
    <div
      v-if="device.isReachable === true"
      class="device-teaser__current-consumption"
      :title="
        str('devices.currentConsumption', {
          '@value': consumption,
        })
      "
    >
      {{ consumption }}
    </div>
  </div>
</template>

<script>
  import { ToggleButton as JsToggle } from 'vue-js-toggle-button'
  import Spinner from '@/components/icons/Spinner'
  import Clock from '@/components/icons/Clock'
  import { COLOR_PRIMARY } from '@/constants'

  export default {
    name: 'device-teaser',
    components: {
      JsToggle,
      Spinner,
      Clock,
    },
    props: {
      device: {
        type: Object,
        required: true,
      },
      showClockWhenTimerIsActive: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        primaryColor: COLOR_PRIMARY,
      }
    },
    computed: {
      targetUrl() {
        return `devices/${this.device.id}`
      },
      imageUrl() {
        return this.assetsPath(
          `device-types/${this.device.deviceType.toLowerCase()}.png`
        )
      },
      consumption() {
        // Only show 0W when needed to get rid of visual clutter.
        if (this.device.isOn || this.device.timer.active) {
          return this.formatWattage(this.device.wattage)
        }
        return ''
      },
      temperature() {
        return this.formatTemperature(this.device.temperature)
      },
    },
  }
</script>

<style lang="scss">
  .device-teaser {
    transition: $transition-base;
    padding: spacer(1) spacer(1) spacer(1) 0;
    margin-bottom: spacer(0.25);
    display: grid;
    $switch-size: 60px;
    grid-template-columns: 70px 1fr $switch-size $switch-size;
    width: 100%;

    @media (max-width: $bp-xs - 1px) {
      grid-auto-rows: 1fr;
    }

    @media (min-width: $bp-xs) {
      grid-template-columns: 85px 1fr $switch-size $switch-size;
    }

    &--not-reachable {
      .device-teaser__img {
        opacity: 0.4;
      }
    }

    &__left {
      margin-right: spacer(1);

      @media (min-width: $bp-xs) {
        grid-row-start: 1;
        grid-row-end: 3;
      }
    }

    &__center {
      display: flex;
      align-items: center;
      flex-wrap: wrap;

      @media (min-width: $bp-xs) {
        align-items: flex-start;
      }
    }

    &__right {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin-left: spacer(0.5);
      flex-grow: 0;
      flex-shrink: 0;
      grid-column-start: 4;
      grid-column-end: 5;

      @media (min-width: $bp-xs) {
        align-items: flex-start;
      }

      @media (min-width: $bp-ssm) {
        margin-left: spacer(1);
      }
    }

    &__current-consumption {
      display: none;
      grid-area: 2 / 3 / 4 / 4;
      margin-left: spacer(0.5);
      margin-top: spacer(0.5);
      font-weight: 900;
      grid-column-start: 4;
      grid-column-end: 5;

      @media (min-width: $bp-xs) {
        display: flex;
        justify-content: flex-end;
      }
    }

    &__current-temperature {
      display: none;
      margin-top: spacer(0.5);
      grid-area: 2 / 2 / 4 / 3;
      justify-content: flex-end;
      grid-column-start: 3;
      grid-column-end: 4;

      &--right {
        grid-column-start: 4;
        grid-column-end: 5;
      }

      @media (min-width: $bp-xs) {
        display: flex;
      }
    }

    &__img {
      max-width: 100%;
    }

    &__title {
      margin: 0;
      line-height: $line-height-min;
      font-size: $font-size-md;
      hyphens: auto;

      @media (min-width: $bp-xs) {
        font-size: $font-size-md;
        line-height: $line-height-sm;
      }

      @media (min-width: $bp-md) {
        font-size: $font-size-lg;
      }
    }

    &__description {
      display: none;
      flex-basis: 100%;
      hyphens: auto;

      @media (min-width: $bp-ssm) {
        display: block;
      }
    }

    &__spinner {
      width: spacer(2);

      .icon {
        animation: spin 1.8s linear 0s infinite;
      }
    }

    &__timer-icon {
      width: spacer(2);
      display: flex;

      @media (min-width: $bp-sm) {
        margin-right: spacer(0.75);
      }
    }
  }
</style>
